/**
 * ============================================================================
 * MASTER PAGE: Platform Authentication & Login Screen
 * ============================================================================
 * Purpose:
 *   Enterprise login interface supporting password security, remember me,
 *   password recovery, and dark/light mode toggle.
 *
 * Database Scope:
 *   Master Database (`digimaster`)
 * ============================================================================
 */
"use client"

import { useState, useEffect, useCallback } from "react"
import { useTheme } from "../../../core/contexts/ThemeContext"
import { Link, useNavigate } from "react-router-dom"
import { EyeIcon, EyeSlashIcon, SunIcon, MoonIcon, ArrowPathIcon, ShieldCheckIcon, BoltIcon, PhoneIcon } from "@heroicons/react/24/outline"
import Swal from "sweetalert2"
import { authService } from "../../services"

const SignIn = () => {
  const navigate = useNavigate()
  const { isDark, toggleTheme } = useTheme()

  const [formData, setFormData] = useState({ email: "", password: "", keepLoggedIn: true })
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  const [agreeTerms, setAgreeTerms] = useState(true)
  const [agreePrivacy, setAgreePrivacy] = useState(true)

  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1)
  const [resetCode, setResetCode] = useState("")
  const [newPassword, setNewPassword] = useState("")

  // 2FA & Dynamic CAPTCHA states
  const [twoFactorToken, setTwoFactorToken] = useState("")
  const [twoFactorCode, setTwoFactorCode] = useState("")
  const [captchaSvg, setCaptchaSvg] = useState("")
  const [captchaId, setCaptchaId] = useState("")
  const [captchaValue, setCaptchaValue] = useState("")
  const [loadingCaptcha, setLoadingCaptcha] = useState(false)

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }))
  }

  const generateLocalCaptcha = useCallback(() => {
    const chars = "23456789ABCDEFGHJKLMNPQRSTUVWXYZ";
    let code = "";
    for (let i = 0; i < 5; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    const expiresAt = Date.now() + 5 * 60 * 1000;
    const token = btoa(`${code}:${expiresAt}`);
    const fallbackId = `fallback_${token}`;

    const width = 150;
    const height = 50;
    const lines = [];
    const lineColors = ["#cbd5e1", "#94a3b8", "#64748b", "#3b82f6", "#6366f1"];
    for (let i = 0; i < 4; i++) {
      const x1 = Math.floor(Math.random() * 25);
      const y1 = Math.floor(Math.random() * height);
      const x2 = Math.floor(width - Math.random() * 25);
      const y2 = Math.floor(Math.random() * height);
      const stroke = lineColors[Math.floor(Math.random() * lineColors.length)];
      lines.push(`<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${stroke}" stroke-width="${(1.2 + Math.random()).toFixed(1)}" stroke-opacity="0.65"/>`);
    }
    const dots = [];
    for (let i = 0; i < 22; i++) {
      const cx = Math.floor(Math.random() * width);
      const cy = Math.floor(Math.random() * height);
      const r = (1 + Math.random() * 1.5).toFixed(1);
      dots.push(`<circle cx="${cx}" cy="${cy}" r="${r}" fill="#94a3b8" opacity="0.35"/>`);
    }
    const charColors = ["#0f172a", "#1e40af", "#4338ca", "#0f766e", "#6d28d9", "#be123c"];
    const textElements = code.split("").map((char, index) => {
      const x = 16 + index * 24 + Math.floor(Math.random() * 4 - 2);
      const y = 33 + Math.floor(Math.random() * 6 - 3);
      const rotate = Math.floor(Math.random() * 26 - 13);
      const color = charColors[index % charColors.length];
      return `<text x="${x}" y="${y}" font-family="Courier New, monospace, sans-serif" font-size="24" font-weight="bold" fill="${color}" transform="rotate(${rotate}, ${x}, ${y})">${char}</text>`;
    }).join("");

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" style="background-color: #f8fafc; border-radius: 8px; user-select: none;">
      <rect width="${width}" height="${height}" fill="#f1f5f9" rx="8"/>
      ${dots.join("")}
      ${lines.join("")}
      ${textElements}
    </svg>`;

    setCaptchaSvg(svg);
    setCaptchaId(fallbackId);
    setCaptchaValue("");
  }, []);

  const fetchCaptcha = useCallback(async () => {
    setLoadingCaptcha(true);
    try {
      const res = await authService.getCaptcha();
      if (res && res.success && res.svg) {
        setCaptchaSvg(res.svg);
        setCaptchaId(res.captchaId);
        setCaptchaValue("");
        return;
      }
      generateLocalCaptcha();
    } catch (err) {
      console.warn("API CAPTCHA unavailable, using client fallback:", err?.message || err);
      generateLocalCaptcha();
    } finally {
      setLoadingCaptcha(false);
    }
  }, [generateLocalCaptcha]);

  useEffect(() => {
    fetchCaptcha()
  }, [fetchCaptcha])

  const handleLogin = async (e) => {
    e.preventDefault()
    setError("")
    setSuccess("")

    const identifier = (formData.email || "").trim()
    const password = (formData.password || "").trim()
    const captcha = (captchaValue || "").trim().toUpperCase()

    if (!identifier || !password) {
      setError("Please enter your Username / Email and Password.")
      return
    }

    if (!captcha) {
      setError("Please enter the CAPTCHA characters shown in the image.")
      return
    }

    setLoading(true)
    try {
      const res = await authService.login({
        username: identifier,
        password: password,
        captchaId: captchaId,
        captchaText: captcha,
      })

      if (res.success) {
        // Condition: User has 2FA enabled -> show 2FA verification step
        if (res.require2FA) {
          setTwoFactorToken(res.twoFactorToken)
          setTwoFactorCode("")
          Swal.fire({
            title: "Two-Factor Verification",
            html: `
              <div style="text-align: left; font-size: 13.5px; line-height: 1.6;">
                <p style="margin-bottom: 8px;">2FA is active on your account.</p>
                <div style="background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 12px; padding: 14px; margin: 10px 0; text-align: center;">
                  <span style="font-size: 11px; font-weight: 700; color: #1e40af; text-transform: uppercase; letter-spacing: 1px; display: block; margin-bottom: 4px;">6-Digit Security Code</span>
                  <strong style="font-size: 26px; letter-spacing: 6px; color: #1d4ed8; font-family: monospace;">${res.previewCode}</strong>
                </div>
                <p style="font-size: 12px; color: #64748b;">Enter this code on the next screen to verify your login.</p>
              </div>
            `,
            icon: "info",
            confirmButtonColor: "#2563eb",
            confirmButtonText: "Enter Code",
          })
          return
        }

        // Condition: Simple login without 2FA -> log in directly
        setSuccess("Login successful! Entering dashboard...")
        if (res.token) {
          localStorage.setItem("token", res.token)
        }
        const userObj = res.user || res.data?.user || (res.data && res.data.str_username ? res.data : null)
        if (userObj) {
          localStorage.setItem("user", JSON.stringify(userObj))
          const accId = userObj.str_account_id || userObj.int_account_id || ""
          if (accId) {
            localStorage.setItem("active_account_id", String(accId))
          }
        }
        setTimeout(() => {
          navigate("/")
        }, 500)
      } else {
        setError(res.message || "Invalid credentials")
        fetchCaptcha()
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to sign in. Please verify your credentials.")
      fetchCaptcha()
    } finally {
      setLoading(false)
    }
  }

  const handleVerify2FA = async (e) => {
    e.preventDefault()
    setError("")
    setSuccess("")

    const cleanCode = (twoFactorCode || "").trim()
    if (!cleanCode || cleanCode.length !== 6) {
      setError("Please enter the complete 6-digit verification code.")
      return
    }

    setLoading(true)
    try {
      const res = await authService.verify2FA({
        twoFactorToken,
        code: cleanCode,
      })

      if (res.success) {
        setSuccess("2FA Verified successfully! Entering dashboard...")
        if (res.token) {
          localStorage.setItem("token", res.token)
        }
        const userObj = res.user || res.data?.user || (res.data && res.data.str_username ? res.data : null)
        if (userObj) {
          localStorage.setItem("user", JSON.stringify(userObj))
          const accId = userObj.str_account_id || userObj.int_account_id || ""
          if (accId) {
            localStorage.setItem("active_account_id", String(accId))
          }
        }
        setTimeout(() => {
          navigate("/")
        }, 500)
      } else {
        setError(res.message || "Invalid 2FA verification code.")
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to verify 2FA code.")
    } finally {
      setLoading(false)
    }
  }

  const resetForgotPasswordFlow = () => {
    setShowForgotPassword(false)
    setForgotPasswordStep(1)
    setResetCode("")
    setNewPassword("")
  }

  const resetTwoFactorFlow = () => {
    setTwoFactorToken("")
    setTwoFactorCode("")
    setError("")
    fetchCaptcha()
  }

  return (
    <div
      className="h-screen w-full flex items-start sm:items-center justify-center px-4 relative overflow-x-hidden overflow-y-auto"
      style={{
        backgroundImage: isDark
          ? `url('https://www.absolutegeeks.com/wp-content/uploads/2024/10/meta.gif')`
          : `url('https://images.squarespace-cdn.com/content/v1/66688035ea92183bf5447f7d/1718124616198-0HSKJJC0H0YESCSQ0A5W/meta_P3_logo_shapeExplore5_3D_v01.gif')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="absolute inset-0 bg-blue-50/70 dark:bg-gray-900/80 backdrop-blur-sm"></div>

      {/* Main Container */}
      <div className="relative z-10 w-full flex">

        {/* Left Column (Branding & Features) */}
        <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 text-gray-900 dark:text-white relative">
          {/* Main Content */}
          <div className="max-w-lg mt-8 z-10">
            <div className="inline-block px-4 py-1.5 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-sm font-semibold mb-6">
              Welcome Back!
            </div>
            <h1 className="text-5xl font-bold mb-2 tracking-tight">Welcome back!</h1>
            <h2 className="text-5xl font-bold text-blue-600 dark:text-blue-400 mb-6 tracking-tight">Glad to see you again</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-12 text-lg">
              Access your account and continue where you left off. Secure, simple and seamless.
            </p>

            <div className="space-y-8">
              {/* Feature 1 */}
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-blue-50 dark:bg-gray-800/80 rounded-xl text-blue-600 dark:text-blue-400 shadow-sm border border-white/40 dark:border-gray-700/50">
                  <ShieldCheckIcon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Secure Access</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">Your data is protected with top-level security</p>
                </div>
              </div>
              {/* Feature 2 */}
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-blue-50 dark:bg-gray-800/80 rounded-xl text-blue-600 dark:text-blue-400 shadow-sm border border-white/40 dark:border-gray-700/50">
                  <BoltIcon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Fast & Simple</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">Quick login experience, minimal steps</p>
                </div>
              </div>
              {/* Feature 3 */}
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-blue-50 dark:bg-gray-800/80 rounded-xl text-blue-600 dark:text-blue-400 shadow-sm border border-white/40 dark:border-gray-700/50">
                  <PhoneIcon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">24/7 Support</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">We're here to help you anytime</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-sm text-gray-500 dark:text-gray-400 mt-12 z-10 font-medium">
            © 2026 Digiforest. All rights reserved.
          </div>
        </div>

        {/* Right Column (Sign In Form) */}
        <div className="w-full lg:w-1/2 flex items-center justify-center relative min-h-full">
          {/* Theme toggle */}
          <div className="absolute top-6 right-6 lg:top-8 lg:right-8 z-20">
            <button
              onClick={toggleTheme}
              className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors rounded-full hover:bg-black/5 dark:hover:bg-white/10"
              title="Toggle theme"
            >
              {isDark ? <SunIcon className="w-6 h-6" /> : <MoonIcon className="w-6 h-6" />}
            </button>
          </div>

          {/* Form Container */}
          <div className="w-full max-w-lg z-10 relative mt-8 mb-8 sm:mt-0">
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 sm:p-8 border border-gray-100 dark:border-gray-700">

              {error && (
                <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg text-red-700 dark:text-red-300 text-sm">
                  {error}
                </div>
              )}

              {success && (
                <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-lg text-green-700 dark:text-green-300 text-sm">
                  {success}
                </div>
              )}

              {!showForgotPassword ? (
                twoFactorToken ? (
                  <>
                    <div className="mb-4">
                      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Two-Factor Verification</h1>
                      <p className="text-gray-600 dark:text-gray-400">Enter the 6-digit code from your authenticator app.</p>
                    </div>
                    <form onSubmit={handleVerify2FA} className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                          6-Digit Verification Code <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={twoFactorCode}
                          onChange={(e) => setTwoFactorCode(e.target.value.replace(/[^0-9]/g, ""))}
                          className="w-full px-3 py-3 bg-gray-50/80 dark:bg-gray-700/80 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 backdrop-blur-sm text-center text-3xl tracking-widest font-mono"
                          placeholder="000000"
                          maxLength="6"
                          disabled={loading}
                          autoFocus
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={loading || twoFactorCode.length !== 6}
                        className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-4 px-4 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 flex items-center justify-center space-x-2 cursor-pointer"
                      >
                        {loading && <ArrowPathIcon className="w-5 h-5 animate-spin" />}
                        <span>{loading ? "Verifying..." : "Verify and sign in"}</span>
                      </button>
                      <button
                        type="button"
                        onClick={resetTwoFactorFlow}
                        className="w-full text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium py-2 transition-colors cursor-pointer text-sm"
                      >
                        ← Back to Simple Login
                      </button>
                    </form>
                  </>
                ) : (
                  <>
                    {/* Form header */}
                    <div className="mb-4">
                      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Sign In</h1>
                      <p className="text-gray-600 dark:text-gray-400">Enter your email and password to sign in!</p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleLogin} className="space-y-6">
                      {/* Email or Username */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                          Username or Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-3 py-3 bg-gray-50/80 dark:bg-gray-700/80 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 backdrop-blur-sm"
                          placeholder="e.g. superadmin or admin@digiforest.in"
                          disabled={loading}
                          required
                        />
                      </div>

                      {/* Password */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            className="w-full px-3 py-3 bg-gray-50/80 dark:bg-gray-700/80 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 pr-12 backdrop-blur-sm"
                            placeholder="Enter your password"
                            disabled={loading}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                          >
                            {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>

                      {/* CAPTCHA */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          CAPTCHA Verification <span className="text-red-500">*</span>
                        </label>
                        <div className="flex flex-col sm:flex-row sm:items-stretch space-y-3 sm:space-y-0 sm:space-x-3">
                          <div className="flex items-stretch space-x-2 w-full sm:w-7/12">
                            <div
                              className="flex-grow bg-white border border-gray-200 dark:border-gray-600 rounded-xl overflow-hidden flex items-center justify-center min-h-[55px] dark:invert dark:hue-rotate-180 transition-all duration-200 shadow-2xs"
                              dangerouslySetInnerHTML={{ __html: captchaSvg || `<div class="p-4 text-xs text-gray-400 animate-pulse">Loading CAPTCHA...</div>` }}
                            />
                            <button
                              type="button"
                              onClick={fetchCaptcha}
                              disabled={loadingCaptcha}
                              className="px-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl transition-colors text-gray-600 dark:text-gray-300 flex-shrink-0 flex items-center justify-center cursor-pointer shadow-2xs"
                              title="Refresh CAPTCHA"
                            >
                              <ArrowPathIcon className={`w-5 h-5 ${loadingCaptcha ? "animate-spin text-blue-600" : ""}`} />
                            </button>
                          </div>
                          <div className="relative w-full sm:w-5/12 flex-shrink-0">
                            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                              <ShieldCheckIcon className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                              type="text"
                              value={captchaValue}
                              onChange={(e) => setCaptchaValue(e.target.value.toUpperCase())}
                              className="w-full h-full pl-10 pr-3 py-3 bg-white dark:bg-gray-700/80 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm font-mono uppercase tracking-widest text-center font-bold"
                              placeholder="CAPTCHA"
                              maxLength={6}
                              required
                            />
                          </div>
                        </div>
                      </div>

                      {/* Keep logged in & Forgot password */}
                      <div className="flex items-center justify-between pt-2">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            name="keepLoggedIn"
                            checked={formData.keepLoggedIn}
                            onChange={handleChange}
                            className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:ring-2 transition-colors"
                          />
                          <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">Keep me logged in</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => setShowForgotPassword(true)}
                          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors font-medium"
                        >
                          Forgot password?
                        </button>
                      </div>

                      {/* Terms & Privacy */}
                      <div className="flex flex-col space-y-3 pt-1 pb-1">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={agreeTerms}
                            onChange={(e) => setAgreeTerms(e.target.checked)}
                            className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:ring-2 transition-colors"
                          />
                          <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">
                            I Agree for <Link to="/terms" target="_blank" className="text-blue-600 dark:text-blue-400 hover:underline">Terms and Condition</Link>
                          </span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={agreePrivacy}
                            onChange={(e) => setAgreePrivacy(e.target.checked)}
                            className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:ring-2 transition-colors"
                          />
                          <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">
                            I Agree for <Link to="/privacy" target="_blank" className="text-blue-600 dark:text-blue-400 hover:underline">Privacy & Policy</Link>
                          </span>
                        </label>
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-4 px-4 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-60 flex items-center justify-center space-x-2"
                      >
                        {loading && <ArrowPathIcon className="w-5 h-5 animate-spin" />}
                        <span>{loading ? "Authenticating..." : "Sign in"}</span>
                      </button>
                    </form>
                  </>
                )
              ) : (
                <>
                  <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                      {forgotPasswordStep === 1 && "Forgot Password"}
                      {forgotPasswordStep === 2 && "Verify Code"}
                      {forgotPasswordStep === 3 && "Reset Password"}
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                      {forgotPasswordStep === 1 && "Enter your email to receive a verification code"}
                      {forgotPasswordStep === 2 && "Enter the 6-digit code sent to your email"}
                      {forgotPasswordStep === 3 && "Enter your new password"}
                    </p>
                  </div>

                  {forgotPasswordStep === 1 && (
                    <form onSubmit={(e) => { e.preventDefault(); setForgotPasswordStep(2) }} className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-4 py-4 bg-gray-50/80 dark:bg-gray-700/80 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                          placeholder="Enter your email"
                        />
                      </div>
                      <button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-4 px-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                        Send Reset Code
                      </button>
                      <button type="button" onClick={resetForgotPasswordFlow} className="w-full text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium py-2 transition-colors">
                        Back to Sign In
                      </button>
                    </form>
                  )}

                  {forgotPasswordStep === 2 && (
                    <form onSubmit={(e) => { e.preventDefault(); setForgotPasswordStep(3) }} className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                          Verification Code <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={resetCode}
                          onChange={(e) => setResetCode(e.target.value)}
                          className="w-full px-4 py-4 bg-gray-50/80 dark:bg-gray-700/80 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-center text-2xl tracking-widest"
                          placeholder="000000"
                          maxLength="6"
                        />
                      </div>
                      <button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-4 px-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                        Verify Code
                      </button>
                    </form>
                  )}

                  {forgotPasswordStep === 3 && (
                    <form onSubmit={(e) => { e.preventDefault(); resetForgotPasswordFlow() }} className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                          New Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-full px-4 py-4 bg-gray-50/80 dark:bg-gray-700/80 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 pr-12"
                            placeholder="Enter new password"
                            minLength="6"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                          >
                            {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                      <button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-4 px-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                        Reset Password
                      </button>
                    </form>
                  )}
                </>
              )}

            </div>
          </div>
        </div>

      </div>
    </div>
  )
}

export default SignIn
